// UX35: lightweight share-intent URL builders for X / Facebook /
// LinkedIn. No SDKs; just the public web-intent URLs each platform
// supports. Each returns a URL you can open in a new tab via
// window.open — mobile browsers hand it off to the installed app
// automatically when available.

export function xShareUrl({ text, url }) {
  const u = new URL('https://twitter.com/intent/tweet')
  if (text) u.searchParams.set('text', text)
  if (url) u.searchParams.set('url', url)
  return u.toString()
}

export function facebookShareUrl({ url }) {
  const u = new URL('https://www.facebook.com/sharer/sharer.php')
  u.searchParams.set('u', url)
  return u.toString()
}

export function linkedinShareUrl({ url }) {
  const u = new URL('https://www.linkedin.com/sharing/share-offsite/')
  u.searchParams.set('url', url)
  return u.toString()
}

// UX35: Civic Receipt card URL. Points at /api/poll-card with the
// tally the client already knows, so the server doesn't have to
// subscribe + aggregate on every share click. Title is passed through
// (?t=…) so the card doesn't depend on a server-side relay fetch —
// previously that fetch would occasionally time out and the card
// would render with the fallback "Verdant poll" title.
export function cardImageUrl({ id, format, resident, includeVote, userVote, title, origin }) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '')
  const u = new URL('/api/poll-card', base || 'https://thepeoplevote.org')
  u.searchParams.set('id', id)
  u.searchParams.set('format', format)
  const r = resident || {}
  u.searchParams.set('y', String(clampPct(r.yes)))
  u.searchParams.set('n', String(clampPct(r.no)))
  u.searchParams.set('u', String(clampPct(r.unsure)))
  if (title) u.searchParams.set('t', String(title).slice(0, 400))
  if (includeVote && userVote) {
    const v = String(userVote).toLowerCase()
    if (['yes', 'no', 'unsure'].includes(v)) {
      u.searchParams.set('v', v)
    }
  }
  return u.toString()
}

export function residentPercentages(tally) {
  const yes    = tally?.Yes?.resident || 0
  const no     = tally?.No?.resident || 0
  const unsure = tally?.Unsure?.resident || 0
  const total = yes + no + unsure
  if (!total) return { yes: 0, no: 0, unsure: 0 }
  return {
    yes:    Math.round((yes    / total) * 100),
    no:     Math.round((no     / total) * 100),
    unsure: Math.round((unsure / total) * 100),
  }
}

// Fetches the image as a blob and triggers a browser download.
// Returns true on success, false on failure (caller surfaces a note).
export async function downloadCardImage(url, filename) {
  try {
    const resp = await fetch(url)
    if (!resp.ok) return false
    const blob = await resp.blob()
    const objUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(objUrl)
    return true
  } catch {
    return false
  }
}

// Opens the device's native share sheet via the Web Share API with
// the card image attached as a File when the browser supports it. On
// mobile this lets the user pick Instagram / TikTok / Messages / etc.
// with the image already there. On desktop (where file-share typically
// is not supported) it degrades to a text + URL share so the share
// sheet still opens with the Verdant link.
//
// Returns 'shared' | 'canceled' | 'unsupported' | 'failed'.
//
// pageUrl is optional: when provided it is attached as the URL on
// text-only shares, so even without the image the recipient gets a
// clickable link back to the poll.
export async function nativeShareCard({ url, filename, text, pageUrl }) {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return 'unsupported'
  }
  const shareText = text || ''
  const textPayload = pageUrl
    ? { text: shareText, url: pageUrl }
    : { text: shareText }

  // Phase 1: try with the image file attached. This is only reachable
  // when the UA both supports canShare AND reports it can accept the
  // file (desktop Chrome, for example, says no to files).
  try {
    const resp = await fetch(url)
    if (resp.ok) {
      const blob = await resp.blob()
      const file = new File([blob], filename, { type: blob.type || 'image/png' })
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ ...textPayload, files: [file] })
          return 'shared'
        } catch (e) {
          if (e?.name === 'AbortError') return 'canceled'
          // Any other failure (NotAllowedError on desktop, etc.) falls
          // through to the text-only attempt below.
        }
      }
    }
  } catch { /* fetch/blob failure — fall through to text-only */ }

  // Phase 2: text-only share. Keeps the share sheet usable on desktop
  // and on browsers that silently refuse files.
  try {
    await navigator.share(textPayload)
    return 'shared'
  } catch (e) {
    if (e?.name === 'AbortError') return 'canceled'
    return 'failed'
  }
}

function clampPct(raw) {
  const n = Math.round(Number(raw))
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(100, n))
}
